// Components/Toast.js
import React, {useEffect} from 'react';
import {AlertTriangle, CheckCircle, Info, X} from 'lucide-react';

export const Toast = ({message, type = 'success', onClose, duration = 3000}) => {
    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const getToastStyles = () => {
        switch (type) {
            case 'success':
                return {
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    textColor: 'text-green-800',
                    icon: <CheckCircle size={18} className="text-green-600"/>
                };
            case 'error':
                return {
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    textColor: 'text-red-800',
                    icon: <AlertTriangle size={18} className="text-red-600"/>
                };
            case 'warning':
                return {
                    bgColor: 'bg-yellow-50',
                    borderColor: 'border-yellow-200',
                    textColor: 'text-yellow-800',
                    icon: <AlertTriangle size={18} className="text-yellow-600"/>
                };
            case 'info':
            default:
                return {
                    bgColor: 'bg-blue-50',
                    borderColor: 'border-blue-200',
                    textColor: 'text-blue-800',
                    icon: <Info size={18} className="text-blue-600"/>
                };
        }
    };

    const styles = getToastStyles();

    return (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div
                className={`${styles.bgColor} ${styles.textColor} border ${styles.borderColor} rounded-lg shadow-lg p-4 max-w-md flex items-start gap-3`}>
                <div className="flex-shrink-0 mt-0.5">
                    {styles.icon}
                </div>
                <div className="flex-1">
                    <p className="font-medium">
                        {/* Ensure we're only rendering strings */}
                        {typeof message === 'string' ? message :
                            message && typeof message === 'object' ? 'An error occurred' :
                                'Notification'}
                    </p>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <X size={16}/>
                    </button>
                )}
            </div>
        </div>
    );
};

// Add some simple CSS animations
const style = document.createElement('style');
style.textContent = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
`;
document.head.appendChild(style);